import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import PortalLayout from "../layout/PortalLayout";
import { usePortalData } from "../context/PortalDataContext";
import { Dialog } from "primereact/dialog";

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
  });
}

function calcRemainingDays(updatedAt, duration) {
  if (!updatedAt || !duration) return null;
  const processed = new Date(updatedAt); processed.setHours(0,0,0,0);
  const expiry = new Date(processed); expiry.setDate(expiry.getDate() + Number(duration));
  const today = new Date(); today.setHours(0,0,0,0);
  return Math.ceil((expiry - today) / 86400000);
}

// ── Sub-components ────────────────────────────────────────────────────────────

function RelativeBanner({ name }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      background: "#eff6ff", border: "1px solid #bfdbfe",
      borderRadius: 10, padding: "10px 16px", marginBottom: 16,
      fontSize: 13, color: "#1e40af",
    }}>
      <i className="pi pi-users" style={{ fontSize: 14, flexShrink: 0 }} />
      <span>Viewing records for <b>{name}</b></span>
    </div>
  );
}

function StatusTag({ status }) {
  const map = {
    pending:    { bg: "#fef3c7", color: "#b45309", icon: "pi-clock",            label: "Pending"    },
    processing: { bg: "#eff6ff", color: "#2563eb", icon: "pi-spin pi-spinner",  label: "Processing" },
    processed:  { bg: "#ecfdf5", color: "#059669", icon: "pi-check-circle",     label: "Processed"  },
    rejected:   { bg: "#fef2f2", color: "#dc2626", icon: "pi-times-circle",     label: "Rejected"   },
  };
  const s = map[status] ?? { bg: "#f3f4f6", color: "#6b7280", icon: "pi-info-circle", label: status };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: s.bg, color: s.color,
      borderRadius: 20, padding: "3px 10px", fontSize: 11.5, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      <i className={`pi ${s.icon}`} style={{ fontSize: 10 }} />
      {s.label}
    </span>
  );
}

function RemainingDaysBadge({ days }) {
  if (days === null) return null;
  let bg, color, icon, label;
  if (days < 0)       { bg="#fef2f2"; color="#dc2626"; icon="pi-exclamation-triangle"; label="Supply ended"; }
  else if (days === 0){ bg="#fef3c7"; color="#b45309"; icon="pi-exclamation-circle";   label="Ends today"; }
  else if (days <= 5) { bg="#fef3c7"; color="#b45309"; icon="pi-clock";                label=`${days}d left`; }
  else                { bg="#ecfdf5"; color="#059669"; icon="pi-calendar";              label=`${days}d left`; }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: bg, color, borderRadius: 20,
      padding: "3px 9px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      <i className={`pi ${icon}`} style={{ fontSize: 10 }} />
      {label}
    </span>
  );
}

function SummaryCards({ counts }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
      {[
        { label: "Pending",    count: counts.pending,    bg: "#fef3c7", color: "#b45309" },
        { label: "Processing", count: counts.processing, bg: "#eff6ff", color: "#2563eb" },
        { label: "Processed",  count: counts.processed,  bg: "#ecfdf5", color: "#059669" },
        { label: "Rejected",   count: counts.rejected,   bg: "#fef2f2", color: "#dc2626" },
      ].map((s) => (
        <div key={s.label} style={{
          background: s.bg, borderRadius: 10, padding: "12px 10px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
        }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.count}</span>
          <span style={{ fontSize: 11, color: s.color, fontWeight: 500, textAlign: "center" }}>{s.label}</span>
        </div>
      ))}
    </div>
  );
}

function RefillCard({ refill, onViewItems, onGoToPrescription }) {
  const remainingDays = refill.status === "processed"
    ? calcRemainingDays(refill.updated_at, refill.duration)
    : null;

  const itemSummary = refill.items?.length === 1
    ? refill.items[0].product
    : refill.items?.length > 1
      ? `${refill.items[0].product} +${refill.items.length - 1} more`
      : "—";

  return (
    <div className="ph-card" style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>

      {/* Row 1: date + status + remaining */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>
          <i className="pi pi-calendar" style={{ fontSize: 10, marginRight: 5 }} />
          {formatDate(refill.date)}
        </span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <StatusTag status={refill.status} />
          {remainingDays !== null && <RemainingDaysBadge days={remainingDays} />}
        </div>
      </div>

      {/* Row 2: medicine + all items button */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 9, flexShrink: 0,
          background: "var(--brand-light)", color: "var(--brand-primary)",
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
        }}>
          <i className="pi pi-tablet" />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13.5, fontWeight: 600, color: "var(--text-primary)",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>
            {itemSummary}
          </div>
          {refill.duration > 0 && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
              {refill.duration} days supply requested
            </div>
          )}
        </div>
        {refill.items?.length > 1 && (
          <button
            onClick={() => onViewItems(refill.items)}
            style={{
              flexShrink: 0, display: "flex", alignItems: "center", gap: 5,
              padding: "5px 10px", borderRadius: 8, fontSize: 11.5, fontWeight: 500,
              background: "var(--surface-bg)", color: "var(--text-secondary)",
              border: "1px solid var(--border-color)", cursor: "pointer", fontFamily: "inherit",
            }}
          >
            <i className="pi pi-list" style={{ fontSize: 10 }} /> All items
          </button>
        )}
      </div>

      {/* Row 3: notes */}
      {refill.notes && (
        <div style={{
          fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.5,
          background: "var(--surface-bg)", borderRadius: 8, padding: "7px 10px",
          borderLeft: "3px solid var(--border-color)",
        }}>
          <i className="pi pi-comment" style={{ fontSize: 10, marginRight: 5, color: "var(--text-muted)" }} />
          {refill.notes}
        </div>
      )}

      {/* Row 4: supply progress bar */}
      {refill.status === "processed" && refill.duration > 0 && remainingDays !== null && remainingDays >= 0 && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--text-muted)", marginBottom: 4 }}>
            <span>Supply remaining</span>
            <span>{Math.max(0, remainingDays)} / {refill.duration} days</span>
          </div>
          <div style={{ height: 5, background: "var(--border-color)", borderRadius: 3, overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 3,
              width: `${Math.min(100, Math.max(0, (remainingDays / refill.duration) * 100))}%`,
              background: remainingDays <= 5 ? "#f59e0b" : "var(--brand-primary)",
              transition: "width 0.4s",
            }} />
          </div>
        </div>
      )}

      {/* Row 5: prescription link */}
      {refill.prescription_id && (
        <div style={{ paddingTop: 6, borderTop: "1px solid var(--border-light)" }}>
          <button
            onClick={() => onGoToPrescription(refill.prescription_id)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "none", border: "none", cursor: "pointer",
              color: "var(--brand-primary)", fontSize: 12.5, fontWeight: 500,
              fontFamily: "inherit", padding: 0,
            }}
          >
            <i className="pi pi-file" style={{ fontSize: 11 }} />
            View linked prescription
            <i className="pi pi-arrow-right" style={{ fontSize: 10, marginLeft: 2 }} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function MyRefills() {
  const [searchParams] = useSearchParams();
  const navigate       = useNavigate();
  const relativeId     = searchParams.get("relative_id");
  const relativeName   = searchParams.get("name");
  const filterId       = searchParams.get("filter_id");

  const [modalItems,   setModalItems]   = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const { refillRequests, loading, refreshRefills } = usePortalData();

  useEffect(() => { refreshRefills(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const allItems = relativeId
    ? refillRequests.filter(r => r.relative_id === relativeId)
    : refillRequests;

  const displayItems = filterId
    ? allItems.filter(r => r.id === filterId)
    : allItems;

  const filtered = statusFilter === "all"
    ? displayItems
    : displayItems.filter(r => r.status === statusFilter);

  const counts = {
    pending:    allItems.filter(i => i.status === "pending").length,
    processing: allItems.filter(i => i.status === "processing").length,
    processed:  allItems.filter(i => i.status === "processed").length,
    rejected:   allItems.filter(i => i.status === "rejected").length,
  };

  const goToPrescription = (prescriptionId) => {
    const params = new URLSearchParams();
    if (relativeId) { params.set("relative_id", relativeId); if (relativeName) params.set("name", relativeName); }
    params.set("filter_rx", prescriptionId);
    navigate(`/prescriptions?${params.toString()}`);
  };

  return (
    <PortalLayout>

      {/* Items dialog — PrimeReact handles its own portal, always renders above everything */}
      <Dialog
        header="Requested Items"
        visible={!!modalItems}
        onHide={() => setModalItems(null)}
        style={{ width: "min(440px, 95vw)" }}
        draggable={false}
        resizable={false}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(modalItems ?? []).map((it, idx) => (
            <div key={idx} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 14px", background: "var(--surface-bg)", borderRadius: 10,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                background: "var(--brand-light)", color: "var(--brand-primary)",
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14,
              }}>
                <i className="pi pi-tablet" />
              </div>
              <span style={{ fontWeight: 500, fontSize: 13, flex: 1 }}>{it.product}</span>
              <span style={{
                background: "var(--surface-card)", border: "1px solid var(--border-color)",
                borderRadius: 6, padding: "2px 8px", fontSize: 12, color: "var(--text-muted)",
              }}>
                Qty: {it.quantity ?? "—"}
              </span>
            </div>
          ))}
        </div>
      </Dialog>

      {relativeId && relativeName && <RelativeBanner name={relativeName} />}

      <div className="page-header">
        <h1 className="page-title">
          {relativeId ? `${relativeName}'s Refills` : "Refill Requests"}
        </h1>
        <p className="page-subtitle">Track the status of your medication refill requests</p>
      </div>

      <SummaryCards counts={counts} />

      {/* Notification filter banner */}
      {filterId && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "var(--brand-light)", border: "1px solid var(--brand-accent)",
          borderRadius: 10, padding: "10px 14px", marginBottom: 12, fontSize: 13,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--brand-primary)" }}>
            <i className="pi pi-filter" style={{ fontSize: 12 }} />
            <span style={{ fontWeight: 500 }}>Showing linked refill request</span>
          </div>
          <button
            onClick={() => {
              const p = new URLSearchParams(searchParams);
              p.delete("filter_id");
              navigate(`/refills?${p.toString()}`, { replace: true });
            }}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              background: "none", border: "1px solid var(--brand-accent)",
              borderRadius: 6, padding: "4px 10px", fontSize: 12, fontWeight: 500,
              color: "var(--brand-primary)", cursor: "pointer", fontFamily: "inherit",
            }}
          >
            <i className="pi pi-times" style={{ fontSize: 10 }} /> Show all
          </button>
        </div>
      )}

      {/* Filter chips */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { key: "all",        label: "All",        count: allItems.length },
          { key: "pending",    label: "Pending",    count: counts.pending    },
          { key: "processing", label: "Processing", count: counts.processing },
          { key: "processed",  label: "Processed",  count: counts.processed  },
          { key: "rejected",   label: "Rejected",   count: counts.rejected   },
        ].map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              padding: "6px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 500,
              border: `1.5px solid ${statusFilter === key ? "var(--brand-primary)" : "var(--border-color)"}`,
              background: statusFilter === key ? "var(--brand-primary)" : "transparent",
              color: statusFilter === key ? "white" : "var(--text-secondary)",
              cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s",
            }}
          >
            {label}
            <span style={{
              background: statusFilter === key ? "rgba(255,255,255,0.25)" : "var(--surface-bg)",
              borderRadius: 10, padding: "0px 6px", fontSize: 11,
            }}>{count}</span>
          </button>
        ))}
      </div>

      {/* Cards */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-spin pi-spinner" style={{ fontSize: 28, display: "block", marginBottom: 12, color: "var(--brand-primary)" }} />
          <div style={{ fontSize: 13 }}>Loading refill requests…</div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", color: "var(--text-muted)" }}>
          <i className="pi pi-inbox" style={{ fontSize: 32, display: "block", marginBottom: 12 }} />
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>
            {statusFilter === "all" ? "No refill requests yet" : `No ${statusFilter} requests`}
          </div>
          <div style={{ fontSize: 13 }}>
            {statusFilter === "all" ? "Go to your prescriptions to request a refill." : "Try viewing all requests instead."}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((refill) => (
            <RefillCard
              key={refill.id}
              refill={refill}
              onViewItems={setModalItems}
              onGoToPrescription={goToPrescription}
            />
          ))}
        </div>
      )}

    </PortalLayout>
  );
}